import { PageHero, SectionHeading, CTABand, Testimonials } from "../components/shared";
import { Reveal } from "../components/Reveal";
import { LogoMark } from "../components/Logo";
import { VALUES, LEADERSHIP, CERTIFICATIONS, IMAGES, CONTACT, OFFICE } from "../data/content";
import { CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";

const About = () => {
  const [leaders, setLeaders] = useState(LEADERSHIP);
  useEffect(() => {
    api.get("/leadership").then(({ data }) => { if (Array.isArray(data) && data.length) setLeaders(data); }).catch(() => {});
  }, []);
  return (
  <>
    <PageHero overline="About Airanet" title="Engineering Reliable Connectivity" subtitle="A telecommunications infrastructure and engineering services company supporting the deployment, operation and maintenance of connected infrastructure across India." image={IMAGES.team} />

    <section className="py-24 bg-white">
      <div className="max-w-[1360px] mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <SectionHeading overline="Company Overview" title="One Delivery Platform" />
          <div className="mt-6 space-y-5 text-lg text-[#64748B] leading-relaxed">
            <p>Airanet India Pvt Ltd is a telecommunications infrastructure and engineering services company supporting the deployment, operation and maintenance of connected infrastructure.</p>
            <p>The company works with telecom operators, OEMs, tower companies, broadband providers, enterprise clients and infrastructure partners. Airanet brings together telecom engineering, field operations, civil and electrical capability, project coordination and technical workforce solutions under a single delivery platform.</p>
          </div>
        </div>
        <div className="lg:col-span-5 grid sm:grid-cols-2 gap-5">
          {[["Our Mission", "To deliver dependable telecom infrastructure and engineering services that help our clients expand, operate and maintain high-performing networks."], ["Our Vision", "To become a trusted Indian telecom infrastructure partner recognised for technical competence, safe execution, responsiveness and consistent project delivery."]].map(([t, d]) => (
            <div key={t} className="bg-[#F4F6F8] border border-[#E2E8F0] rounded-lg p-6">
              <h3 className="font-head font-semibold text-lg text-[#0B1F3A]">{t}</h3>
              <p className="mt-3 text-sm text-[#64748B] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-24 bg-white border-t border-[#E2E8F0]">
      <div className="max-w-[1360px] mx-auto px-5 md:px-8">
        <div className="flex items-center gap-4">
          <span className="grid place-items-center h-14 w-14 rounded-lg bg-white ring-1 ring-[#E2E8F0] shadow-sm"><LogoMark className="h-10 w-10" /></span>
          <div>
            <SectionHeading overline="Inside Airanet" title="Our Office & Capability" />
          </div>
        </div>
        <p className="mt-5 text-[#1261A0] font-head font-semibold text-lg">{CONTACT.tagline}</p>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {OFFICE.map((o, i) => (
            <Reveal key={i} delay={(i % 4) * 0.08} className={i === 0 ? "md:col-span-2 lg:col-span-2 lg:row-span-2" : ""}>
              <figure className={`group relative overflow-hidden rounded-lg border border-[#E2E8F0] ${i === 0 ? "h-full min-h-[280px]" : "h-64"}`}>
                <img src={o.url} alt={o.caption} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/85 via-transparent to-transparent" />
                <span className="absolute top-3 left-3 bg-[#F28C28] text-[#0B1F3A] text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded">{o.tag}</span>
                <figcaption className="absolute bottom-0 inset-x-0 p-4 text-white text-sm">{o.caption}</figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-sm text-[#64748B]">Registered office: {CONTACT.address}.</p>
      </div>
    </section>

    <section className="py-24 bg-[#F4F6F8]">
      <div className="max-w-[1360px] mx-auto px-5 md:px-8">
        <SectionHeading overline="Our Values" title="What Guides Our Work" center />
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-5">
          {VALUES.map((v, i) => (
            <Reveal key={v} delay={(i % 4) * 0.06}>
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 text-center hover-lift hover:border-[#1EA7E1]">
                <span className="font-mono2 text-xs text-[#1261A0]">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-3 font-head font-semibold text-[#0B1F3A]">{v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-24 bg-white">
      <div className="max-w-[1360px] mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14">
        <div>
          <SectionHeading overline="Leadership" title="Our Leadership Team" sub="Profiles are managed in the CMS and shown once approved." />
          <div className="mt-8 space-y-5">
            {leaders.map((l) => (
              <div key={l.id || l.role} className="bg-[#F4F6F8] border border-[#E2E8F0] rounded-lg p-5 sm:p-6" data-testid="leader-card">
                <div className="flex flex-col sm:flex-row gap-5">
                  {l.image ? (
                    <img src={l.image} alt={l.name} className="h-56 w-44 sm:h-52 sm:w-40 rounded-md object-cover object-top shrink-0" />
                  ) : (
                    <span className="h-56 w-44 sm:h-52 sm:w-40 rounded-md bg-[#0B1F3A] text-[#1EA7E1] font-head font-bold text-4xl grid place-items-center shrink-0">{(l.name || "A").charAt(0)}</span>
                  )}
                  <div className="min-w-0">
                    <p className="font-head font-semibold text-xl text-[#0B1F3A]">{l.name}</p>
                    <p className="text-sm font-medium text-[#1261A0]">{l.role}</p>
                    {l.bio && (
                      <div className="mt-3 space-y-2.5 text-[13px] text-[#64748B] leading-relaxed">
                        {l.bio.split(/\n\n+/).map((p, i) => <p key={i}>{p}</p>)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <SectionHeading overline="Operating Approach & Coverage" title="Certifications & Registrations" sub="Placeholders shown for client approval. No certification is claimed without proof." />
          <div className="mt-8 flex flex-wrap gap-3">
            {CERTIFICATIONS.map((c) => (
              <span key={c} className="inline-flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-full px-4 py-2 text-sm text-[#252B33]"><CheckCircle2 className="h-4 w-4 text-[#1261A0]" /> {c}</span>
            ))}
          </div>
          <div className="mt-8 bg-[#0B1F3A] text-white rounded-lg p-6">
            <h4 className="font-head font-semibold text-[#1EA7E1]">Corporate Information</h4>
            <p className="mt-3 text-sm text-[#CBD5E1]">Registered Office: {CONTACT.address}</p>
            <p className="mt-1 text-sm text-[#CBD5E1]">Registration No: {CONTACT.reg}</p>
            <p className="mt-1 text-sm text-[#CBD5E1]">Geographic coverage: pan-India (service locations to be confirmed).</p>
          </div>
        </div>
      </div>
    </section>
    <Testimonials />
    <CTABand />
  </>
  );
};

export default About;
